import {Button, Stack} from "@mui/material";
import {useDataTableStorage, useSpringPage, useSpringPageable, useStorage} from "@variocube/app-ui";
import React, {Fragment, useMemo} from "react";
import {useAsync} from "react-async-hook";
import {Link as RouterLink} from "react-router-dom";
import {useBookingApi} from "../../api";
import {useLocalization, useWordingFlavor} from "@smalox/shared";
import {useAnyPermission} from "../../tenant";
import {View, ViewHeader} from "../View";
import {BookingFilter} from "./BookingFilter";
import {BookingRetrievalConfirmation} from "./BookingRetrievalConfirmation";
import BookingTable from "./BookingTable";

const defaultFilter: BookingFilter = {status: "ACTIVE"};

export function BookingList() {
	const {t} = useLocalization();
	const wordingFlavor = useWordingFlavor();

	const canCreate = useAnyPermission("BOOKING_CREATE", "BOOKING_CREATE_OWN");
	const canEdit = useAnyPermission("BOOKING_EDIT");

	return (
		<View>
			<ViewHeader
				title={t(`bookings.title.${wordingFlavor}`)}
				actions={
					<Stack direction="row" spacing={1}>
						{canEdit && (
							<Button variant="outlined" color="error" component={RouterLink} to="bulk-end">
								{t(`bookings.bulkEnd.title.${wordingFlavor}`)}
							</Button>
						)}
						{canCreate && (
							<Button variant="contained" color="primary" component={RouterLink} to="new">
								{t(`bookings.create.title.${wordingFlavor}`)}
							</Button>
						)}
					</Stack>
				}
			/>
			<BookingRetrievalConfirmation>
				<BookingListConfirmed />
			</BookingRetrievalConfirmation>
		</View>
	);
}

function BookingListConfirmed() {
	const {queryBookings} = useBookingApi();

	const {onPageChange, onSort, ...storage} = useDataTableStorage("BookingList.paging");
	const pageable = useSpringPageable(storage);

	const [filter, setFilter] = useStorage<BookingFilter>("BookingList.filter", defaultFilter);

	const query = useMemo(() => ({
		...filter,
		...pageable,
	}), [filter, pageable]);

	const {loading, error, result} = useAsync(queryBookings, [query]);
	const {rows, page} = useSpringPage(result);

	return (
		<Fragment>
			<BookingFilter value={filter} onChange={setFilter} />
			<BookingTable
				loading={loading}
				error={error}
				rows={rows}
				page={page}
				onSort={onSort}
				onPageChange={onPageChange}
				storage={storage}
			/>
		</Fragment>
	);
}
